import React from "react";
import { CheckCircle2, Star, Users, Sparkles, Crown, BookOpen, Download, ClipboardList, Repeat, Clock, Bot, PenTool, Link2 } from "lucide-react";
import { Button } from "./Button";
import { landingContent } from "./i18n";

// Ablefy-Zahlungslinks (Platzhalter – werden nachgeliefert)
const WF_CHECKOUT = {
  starter: "https://myablefy.com/s/femmesmarketingstudio/kopie-von-angebots-rechnungs-studio-starter-0075396a/payment",
  pro: "https://myablefy.com/s/femmesmarketingstudio/kopie-von-angebots-rechnungs-studio-pro-9cfd43cc/payment",
  business: "https://myablefy.com/s/femmesmarketingstudio/kopie-von-angebots-rechnungs-studio-business-603e894d/payment",
};

// Sprachunabhängige Metadaten
const WF_META = {
  starter: { icon: Users, price: "49", highlight: false },
  pro: { icon: Sparkles, price: "79", highlight: true },
  business: { icon: Crown, price: "119", highlight: false },
};

const FEATURE_ICONS = [ClipboardList, Repeat, Clock, Bot, PenTool, Link2];

// Vergleichs-Matrix (s = Starter, p = Pro, b = Business)
const WF_COMPARE = [
  { s: true, p: true, b: true },   // Mitarbeiterverwaltung
  { s: true, p: true, b: true },   // Einsatzplanung
  { s: true, p: true, b: true },   // Urlaubskalender
  { s: true, p: true, b: true },   // Projektverwaltung
  { s: true, p: true, b: true },   // Fahrzeugverwaltung
  { s: true, p: true, b: true },   // Dokumentenverwaltung
  { s: false, p: true, b: true },  // Zeiterfassung
  { s: false, p: true, b: true },  // Digitaler Stundenzettel
  { s: false, p: true, b: true },  // Wiederkehrende Einsätze
  { s: false, p: true, b: true },  // Auswertungen
  { s: false, p: true, b: true },  // Multi-User
  { s: false, p: false, b: true }, // KI-Einsatzbericht
  { s: false, p: false, b: true }, // Digitale Kundenunterschrift
  { s: false, p: false, b: true }, // Teamchat
  { s: false, p: false, b: true }, // Push-Benachrichtigungen
  { s: false, p: false, b: true }, // Prioritäts-Support
];

const WorkforceSection = ({ onLogin, lang = "de" }) => {
  const t = landingContent[lang].workforce;
  const guideBtn = landingContent[lang].guideBtn;
  const goWf = (url) => { if (url) window.open(url, "_blank", "noopener,noreferrer"); else if (onLogin) onLogin(); };
  const API = process.env.REACT_APP_BACKEND_URL;
  const openPdf = (file) => window.open(API ? `${API}/api/pdf/${file}` : `/pdf/${file}`, "_blank", "noopener,noreferrer");

  return (
    <section className="px-6 lg:px-12 py-16" data-testid="landing-workforce">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-2"><span className="inline-block text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold">{t.eyebrow}</span></div>
        <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-2">{t.heading}</h2>
        <p className="text-center text-[var(--brand-ink)] font-medium mb-3">{t.tagline}</p>
        <p className="text-center text-[var(--brand-muted)] mb-6 max-w-2xl mx-auto">{t.sub}</p>
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <Button onClick={() => openPdf("Project_Workforce_Studio.pdf")} variant="outline" className="border border-[var(--brand-border)] text-[var(--brand-ink)] hover:bg-[var(--brand-border-light)] rounded-full px-7 py-2.5 font-semibold bg-transparent" data-testid="wf-pdf-btn"><Download className="w-4 h-4 mr-2 inline-block" /> {t.pdfBtn}</Button>
          <Button onClick={() => openPdf("Anleitung_Project_Workforce_Studio.pdf")} variant="outline" className="border border-[var(--brand-border)] text-[var(--brand-ink)] hover:bg-[var(--brand-border-light)] rounded-full px-7 py-2.5 font-semibold bg-transparent" data-testid="wf-guide-btn"><BookOpen className="w-4 h-4 mr-2 inline-block" /> {guideBtn}</Button>
        </div>

        {/* Feature-Highlights */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {t.features.map((f, i) => {
            const Icon = FEATURE_ICONS[i] || ClipboardList;
            return (
              <div key={f.title} className="p-5 rounded-xl border border-[var(--brand-border)] hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: "var(--brand-border-light)" }}>
                  <Icon className="w-5 h-5 text-[var(--brand-primary)]" />
                </div>
                <h3 className="font-semibold text-[var(--brand-ink)] mb-1">{f.title}</h3>
                <p className="text-sm text-[var(--brand-muted)]">{f.text}</p>
              </div>
            );
          })}
        </div>

        {/* Preise */}
        <div className="mt-12">
          <div className="text-center mb-2"><span className="inline-block text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold">{t.pkgEyebrow}</span></div>
          <h3 className="font-[Playfair_Display] text-2xl font-bold text-center text-[var(--brand-ink)] mb-3">{t.pkgHeading}</h3>
          <p className="text-center text-[var(--brand-muted)] mb-8 max-w-2xl mx-auto">{t.pkgSub}</p>

          <div className="flex flex-wrap justify-center gap-3 mb-10" data-testid="wf-price-glance">
            {t.plans.map((p) => {
              const m = WF_META[p.key];
              return (
                <div key={p.key} className={`flex items-baseline gap-2 rounded-full border px-5 py-2.5 ${m.highlight ? "bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white" : "bg-white border-[var(--brand-border)] text-[var(--brand-ink)]"}`}>
                  <span className={`text-xs uppercase tracking-wider font-semibold ${m.highlight ? "text-white/80" : "text-[var(--brand-muted)]"}`}>{p.name}</span>
                  <span className="text-lg font-bold">{m.price} €</span>
                  <span className={`text-xs ${m.highlight ? "text-white/70" : "text-[var(--brand-muted)]"}`}>{p.per}</span>
                </div>
              );
            })}
          </div>

          <div className="space-y-6">
            {t.plans.map((p) => {
              const m = WF_META[p.key];
              const Icon = m.icon;
              return (
                <div key={p.key} className={`relative rounded-2xl overflow-hidden border grid grid-cols-1 md:grid-cols-5 bg-white ${m.highlight ? "border-[var(--brand-primary)] shadow-xl" : "border-[var(--brand-border)]"}`} data-testid={`wf-plan-${p.key}`}>
                  {m.highlight && (<span className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 bg-white text-[var(--brand-ink)] text-xs px-3 py-1 rounded-full shadow"><Star className="w-3.5 h-3.5 fill-[var(--brand-primary)] text-[var(--brand-primary)]" /> {t.recommended}</span>)}
                  <div className="md:col-span-3 p-8 lg:p-10">
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold mb-2"><Icon className="w-4 h-4" /> {p.name}</span>
                    <h4 className="font-[Playfair_Display] text-2xl font-bold text-[var(--brand-ink)] mb-2">Project & Workforce Studio {p.name}</h4>
                    <p className="text-sm text-[var(--brand-muted)] mb-5">{p.tagline}</p>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-muted)] mb-3">{p.featsTitle}</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                      {p.feats.map((ft) => (<li key={ft} className="flex items-start gap-2 text-sm text-[var(--brand-ink)]"><CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> {ft}</li>))}
                    </ul>
                  </div>
                  <div className="md:col-span-2 p-8 lg:p-10 flex flex-col justify-center text-white" style={{ background: m.highlight ? "linear-gradient(160deg, #5B636C 0%, #3E454C 100%)" : "linear-gradient(160deg, #7A828B 0%, #565E66 100%)" }}>
                    <p className="text-xs uppercase tracking-wider text-white/70">{t.monthlyRent}</p>
                    <div className="my-2"><span className="text-5xl font-bold">{m.price} €</span><span className="text-white/75">{p.per}</span></div>
                    <Button onClick={() => goWf(WF_CHECKOUT[p.key])} className="w-full rounded-full bg-white text-[var(--brand-ink)] hover:bg-white/90 font-semibold" data-testid={`wf-start-${p.key}`}>{WF_CHECKOUT[p.key] ? t.buyNow : t.startNow}</Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Vergleichstabelle */}
        <div className="mt-12" data-testid="wf-compare">
          <h3 className="font-[Playfair_Display] text-2xl font-bold text-center text-[var(--brand-ink)] mb-2">{t.compareHeading}</h3>
          <p className="text-center text-[var(--brand-muted)] mb-6">{t.compareSub}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-[var(--brand-border)]">
                  <th className="text-left py-3 px-4 font-semibold text-[var(--brand-ink)]">{t.colFeature}</th>
                  <th className="text-center py-3 px-4 font-semibold text-[var(--brand-ink)]">Starter</th>
                  <th className="text-center py-3 px-4 font-semibold text-[var(--brand-primary)]">Pro</th>
                  <th className="text-center py-3 px-4 font-semibold text-[var(--brand-ink)]">Business</th>
                </tr>
              </thead>
              <tbody>
                {t.compareRows.map((row, i) => {
                  const c = WF_COMPARE[i];
                  return (
                    <tr key={row} className="border-b border-[var(--brand-border)]/50">
                      <td className="py-2.5 px-4 text-[var(--brand-ink)]">{row}</td>
                      <td className="text-center py-2.5 px-4">{c.s ? <CheckCircle2 className="w-4 h-4 text-green-600 mx-auto" /> : <span className="text-[var(--brand-muted)]">&ndash;</span>}</td>
                      <td className="text-center py-2.5 px-4">{c.p ? <CheckCircle2 className="w-4 h-4 text-green-600 mx-auto" /> : <span className="text-[var(--brand-muted)]">&ndash;</span>}</td>
                      <td className="text-center py-2.5 px-4">{c.b ? <CheckCircle2 className="w-4 h-4 text-green-600 mx-auto" /> : <span className="text-[var(--brand-muted)]">&ndash;</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Zielgruppen */}
        <div className="mt-12 rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-border-light)] p-6 lg:p-8" data-testid="wf-audience">
          <h3 className="font-[Playfair_Display] text-xl font-bold text-[var(--brand-ink)] mb-4">{t.audienceHeading}</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {t.audienceItems.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-[var(--brand-ink)]"><CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> {item}</li>
            ))}
          </ul>
        </div>

        <p className="text-center text-xs text-[var(--brand-muted)] mt-8 max-w-2xl mx-auto">{t.servicesNote}</p>
      </div>
    </section>
  );
};

export default WorkforceSection;
